import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

let cachedIds = null;
let cachePromise = null;

export function useFavorites() {
  const { user } = useAuth();
  const [favoriteIds, setFavoriteIds] = useState(cachedIds || []);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!user) {
      setFavoriteIds([]);
      cachedIds = null;
      return;
    }
    if (cachedIds) {
      setFavoriteIds(cachedIds);
      return;
    }
    if (cachePromise) {
      const ids = await cachePromise;
      setFavoriteIds(ids);
      return;
    }
    setLoading(true);
    cachePromise = api.get("/wishlist/ids").then((r) => {
      cachedIds = r.data;
      return r.data;
    }).finally(() => {
      cachePromise = null;
    });
    try {
      const ids = await cachePromise;
      setFavoriteIds(ids);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const invalidateCache = useCallback(() => {
    cachedIds = null;
    load();
  }, [load]);

  const toggle = useCallback(
    async (courtId) => {
      if (!user) {
        toast.error("Sign in to save favorites");
        return;
      }
      const isFav = favoriteIds.includes(courtId);
      try {
        if (isFav) {
          await api.delete(`/wishlist/${courtId}`);
          setFavoriteIds((prev) => prev.filter((id) => id !== courtId));
          cachedIds = cachedIds?.filter((id) => id !== courtId) || null;
        } else {
          await api.post("/wishlist", { court_id: courtId });
          setFavoriteIds((prev) => [...prev, courtId]);
          cachedIds = cachedIds ? [...cachedIds, courtId] : null;
        }
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    },
    [user, favoriteIds]
  );

  return { favoriteIds, toggle, loading, reload: invalidateCache };
}
